import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, RefreshCcw, Palette, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-[#877E78] uppercase">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="color"
          value={value || '#000000'}
          onChange={e => onChange(e.target.value)}
          className="h-9 w-12 rounded border border-[#E8E3D9] cursor-pointer"
          data-testid={testid ? `${testid}-picker` : undefined}
        />
        <Input
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="h-9 flex-1 text-xs font-mono"
          placeholder="#C25934"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function SliderField({ label, value, min, max, onChange, suffix = 'px', testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-bold text-[#877E78] uppercase">{label}</label>
        <span className="text-[11px] font-mono text-[#5C554F]">{value}{suffix}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-[#C25934] cursor-pointer"
        data-testid={testid}
      />
    </div>
  );
}

export default function CardTemplateManager({ getHeaders }) {
  const [data, setData] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const debounceRef = useRef(null);
  const logoInputRef = useRef(null);

  const auth = () => (getHeaders ? getHeaders() : {});

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/shops/mine/card-template`, { withCredentials: true, headers: auth() });
      setData(data);
      setTpl(data.template);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refreshPreview = useCallback(async (overrides) => {
    setPreviewLoading(true);
    try {
      const r = await axios.post(
        `${API}/shops/mine/card-template/preview`,
        { template_overrides: overrides },
        { withCredentials: true, headers: auth(), responseType: 'blob' }
      );
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(r.data);
      });
    } catch (_) { /* silent */ }
    finally { setPreviewLoading(false); }
    // eslint-disable-next-line
  }, []);

  // Debounced preview when template changes
  useEffect(() => {
    if (!tpl) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { refreshPreview(tpl); }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [tpl, refreshPreview]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const update = (k, v) => setTpl(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/shops/mine/card-template`, tpl, { withCredentials: true, headers: auth() });
      toast.success('Template salvato. I prossimi prodotti useranno questo stile.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (!data?.defaults) return;
    setTpl({ ...data.defaults });
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/shops/mine/brand-kit/logo`, fd, {
        withCredentials: true,
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo caricato. Refresh anteprima...');
      await load();
      // force preview refresh
      refreshPreview(tpl || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!data || !tpl) return (
    <div className="bg-white rounded-2xl border border-[#E8E3D9] p-4 flex items-center justify-center">
      <Loader2 className="animate-spin text-[#C25934]" size={20} />
    </div>
  );

  return (
    <div data-testid="card-template-manager" className="bg-white rounded-2xl border border-[#E8E3D9] p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-[#2A2624] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <Palette size={18} className="text-[#C25934]" /> Template Card Prodotto
        </h2>
        <Button
          data-testid="card-tpl-reset-btn"
          onClick={resetDefaults}
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-[#877E78] hover:text-[#C25934]"
        >
          <RefreshCcw size={12} className="mr-1" /> Default
        </Button>
      </div>

      <p className="text-xs text-[#877E78]">
        Personalizza la grafica delle card che genera il sistema (volantino + Open Graph).
        Tutto è editabile: colori, layout, font, dimensioni, logo. L'anteprima si aggiorna in tempo reale.
      </p>

      {/* MASTER toggle: enable/disable card generation */}
      <div className={`rounded-xl p-3 flex items-start gap-3 ${tpl.enabled ? 'bg-[#4A6B53]/5 border border-[#4A6B53]/30' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#2A2624]">Genera scheda prodotto</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${tpl.enabled ? 'bg-[#4A6B53] text-white' : 'bg-amber-500 text-white'}`}>
              {tpl.enabled ? 'ATTIVO' : 'DISATTIVO'}
            </span>
          </div>
          <p className="text-[11px] text-[#5C554F] mt-0.5">
            {tpl.enabled
              ? 'Al caricamento di un nuovo prodotto viene creata anche una scheda grafica con titolo, prezzo e logo.'
              : 'Le foto caricate vengono salvate così come sono — niente scheda grafica generata automaticamente.'}
          </p>
        </div>
        <button
          data-testid="card-tpl-enabled"
          onClick={() => update('enabled', !tpl.enabled)}
          className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${tpl.enabled ? 'bg-[#4A6B53]' : 'bg-[#D5CFC4]'}`}
        >
          <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${tpl.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {!tpl.enabled && (
        <div className="text-xs text-[#877E78] italic text-center py-2">
          Attiva la generazione della scheda per personalizzare l'aspetto.
        </div>
      )}

      {tpl.enabled && (
      <div className="grid lg:grid-cols-2 gap-4">
        {/* CONTROLS */}
        <div className="space-y-3">
          {/* Preset */}
          <div>
            <label className="text-[10px] font-bold text-[#877E78] uppercase">Layout</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {data.presets.map(p => (
                <button
                  key={p}
                  data-testid={`card-tpl-preset-${p}`}
                  onClick={() => update('preset', p)}
                  className={`h-9 rounded-lg text-xs font-semibold border transition ${tpl.preset === p ? 'border-[#C25934] bg-[#C25934]/10 text-[#C25934]' : 'border-[#E8E3D9] bg-white text-[#5C554F] hover:bg-[#F0EBE1]'}`}
                >
                  {p === 'classic' ? 'Classico' : p === 'framed' ? 'Cornice' : 'Bold'}
                </button>
              ))}
            </div>
          </div>

          {/* Font family */}
          <div>
            <label className="text-[10px] font-bold text-[#877E78] uppercase">Carattere</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {(data.font_families || []).map(f => (
                <button
                  key={f.key}
                  data-testid={`card-tpl-font-${f.key}`}
                  onClick={() => update('font_family', f.key)}
                  className={`h-9 rounded-lg text-xs font-semibold border transition ${tpl.font_family === f.key ? 'border-[#C25934] bg-[#C25934]/10 text-[#C25934]' : 'border-[#E8E3D9] bg-white text-[#5C554F] hover:bg-[#F0EBE1]'}`}
                  style={{ fontFamily: f.key === 'serif' ? 'serif' : f.key === 'mono' ? 'monospace' : 'sans-serif' }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Font sizes */}
          <div className="rounded-xl border border-[#E8E3D9] p-3 space-y-3">
            <SliderField label="Dimensione titolo" value={tpl.font_size_title} min={18} max={48} onChange={v => update('font_size_title', v)} testid="card-tpl-title-size" />
            <SliderField label="Dimensione prezzo" value={tpl.font_size_price} min={30} max={80} onChange={v => update('font_size_price', v)} testid="card-tpl-price-size" />
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-2">
            <ColorField label="Sfondo" value={tpl.bg_color} onChange={v => update('bg_color', v)} testid="card-tpl-bg" />
            <ColorField label="Accento" value={tpl.accent_color} onChange={v => update('accent_color', v)} testid="card-tpl-accent" />
            <ColorField label="Prezzo" value={tpl.price_color} onChange={v => update('price_color', v)} testid="card-tpl-price" />
            <ColorField label="Sconto" value={tpl.discount_bg_color} onChange={v => update('discount_bg_color', v)} testid="card-tpl-discount" />
            <ColorField label="Footer" value={tpl.footer_bg_color} onChange={v => update('footer_bg_color', v)} testid="card-tpl-footer" />
            <ColorField label="Testo footer" value={tpl.footer_text_color} onChange={v => update('footer_text_color', v)} testid="card-tpl-footer-text" />
          </div>

          {/* Logo block */}
          <div className="rounded-xl border border-[#E8E3D9] p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-[#2A2624]">Logo del negozio</span>
              <Button
                data-testid="card-tpl-upload-logo-btn"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                variant="outline"
                size="sm"
                className="h-7 text-xs rounded-full"
              >
                {uploadingLogo ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                {data.has_logo ? 'Sostituisci' : 'Carica logo'}
              </Button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-[#5C554F]">Mostra logo</span>
              <button
                data-testid="card-tpl-show-logo"
                onClick={() => update('show_logo', !tpl.show_logo)}
                disabled={!data.has_logo}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${tpl.show_logo ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'bg-[#F0EBE1] text-[#877E78]'} ${!data.has_logo ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                {tpl.show_logo ? 'Visibile' : 'Nascosto'}
              </button>
            </div>

            {tpl.show_logo && data.has_logo && (
              <>
                <div>
                  <label className="text-[10px] font-bold text-[#877E78] uppercase">Posizione</label>
                  <div className="grid grid-cols-3 gap-1 mt-1">
                    {['top-left', 'top-right', 'footer'].map(pos => (
                      <button
                        key={pos}
                        data-testid={`card-tpl-logo-${pos}`}
                        onClick={() => update('logo_position', pos)}
                        className={`h-8 rounded-lg text-[10px] font-semibold border ${tpl.logo_position === pos ? 'border-[#C25934] bg-[#C25934]/10 text-[#C25934]' : 'border-[#E8E3D9] text-[#5C554F]'}`}
                      >
                        {pos === 'top-left' ? '↖ Alto sx' : pos === 'top-right' ? '↗ Alto dx' : '⬇ Footer'}
                      </button>
                    ))}
                  </div>
                </div>

                <SliderField label="Dimensione logo" value={tpl.logo_size} min={24} max={128} onChange={v => update('logo_size', v)} testid="card-tpl-logo-size" />

                {(tpl.logo_position === 'top-left' || tpl.logo_position === 'top-right') && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#5C554F]">Cornice bianca dietro al logo</span>
                    <button
                      data-testid="card-tpl-logo-frame"
                      onClick={() => update('logo_frame', !tpl.logo_frame)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${tpl.logo_frame ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'bg-[#F0EBE1] text-[#877E78]'}`}
                    >
                      {tpl.logo_frame ? 'Con cornice' : 'Senza cornice'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Toggles elementi visibili */}
          <div>
            <label className="text-[10px] font-bold text-[#877E78] uppercase mb-1 block">Elementi visibili</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { k: 'show_frame', l: 'Cornice foto' },
                { k: 'show_title', l: 'Titolo' },
                { k: 'show_price', l: 'Prezzo' },
                { k: 'show_discount', l: 'Sconto' },
                { k: 'show_variants', l: 'Varianti' },
                { k: 'show_footer', l: 'Footer scuro' },
                { k: 'show_shop_name', l: 'Nome negozio' },
                { k: 'show_category', l: 'Categoria' },
              ].map(({ k, l }) => (
                <button
                  key={k}
                  data-testid={`card-tpl-${k}`}
                  onClick={() => update(k, !tpl[k])}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${tpl[k] ? 'bg-[#4A6B53]/10 text-[#4A6B53] border-[#4A6B53]/30' : 'bg-[#F0EBE1] text-[#877E78] border-[#E8E3D9]'}`}
                >
                  {tpl[k] ? '✓' : '✕'} {l}
                </button>
              ))}
            </div>
          </div>

          <Button
            data-testid="card-tpl-save-btn"
            onClick={save}
            disabled={saving}
            className="w-full h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white"
          >
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
            Salva template
          </Button>
        </div>

        {/* PREVIEW */}
        <div className="relative">
          <p className="text-[10px] font-bold text-[#877E78] uppercase mb-1.5">Anteprima live</p>
          <div className="aspect-[4/5] rounded-xl border border-[#E8E3D9] overflow-hidden bg-[#F0EBE1] relative sticky top-2">
            {previewUrl && (
              <img
                data-testid="card-tpl-preview-img"
                src={previewUrl}
                alt="Anteprima card"
                className="w-full h-full object-contain"
              />
            )}
            {previewLoading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                <Loader2 className="animate-spin text-[#C25934]" size={28} />
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
